import React,{useState,useEffect} from "react";
import PageHelmet from "../components/PageHelmet.jsx";
import CustomDataGrid from "../components/CustomDataGrid.jsx";
import {
      generateGridRows,
      generateGridColumns,
      existingQuotaExtensionsGridColumns,
      quotaExtensionsOptionalColumns,
      existingQuotaExtensionsRowMapping,
      quotaExtensionMapping,
      generateFormData,
      } from "../utils/PageUtils.js";
import api from "./PlanMock.js";
import EntityDetails from "../components/EntityDetails.jsx";
import '../styles/AddOns.css';
import QuotaBarChart from "../components/Barchart";
import { FiFileText, FiBarChart2 } from "react-icons/fi"; // icons for tabs
import { MdArrowBackIos } from "react-icons/md";
import { Alert, Tab, Tabs } from "react-bootstrap";

function QuotaExtensions({userRole, bearerToken}) {
    const [quotaExtensions, setQuotaExtensions] = useState([]);
    const [quotaExtensionError, setQuotaExtensionError] = useState(null);
    const [selectedRowId, setSelectedRowId] = useState(null);
    const [selectedQuota, setSelectedQuota] = useState(null); 
    const [quotaData, setQuotaData] = useState([]);
    const [activeTab, setActiveTab] = useState("details"); // Default tab

    // Mobile detection and body scroll lock
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
      const checkMobile = () => setIsMobile(window.innerWidth <= 768);
      checkMobile();
      window.addEventListener('resize', checkMobile);
      return () => window.removeEventListener('resize', checkMobile);
    }, []);

    useEffect(() => {
      if (selectedQuota && isMobile) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = '';
      }
      return () => { document.body.style.overflow = ''; };
    }, [selectedQuota, isMobile]);

   useEffect(() =>{
     const fetchQuotaExtensions = async() =>{
        try{
                const response = await api.get(`/quota-extension`);
                console.log("Quota extensions response", response)
                if(response.status == 200){
                    setQuotaExtensions(response.data)
                    // Don't auto-select on mobile; let user click a row
                    if (!isMobile) {
                      setSelectedQuota(response.data[0]); // Select first row by default on desktop
                      setSelectedRowId(1);
                    }
                } else{
                    setQuotaExtensionError("Error while fetching quota extensions")
                }
        }catch(e){
            setQuotaExtensionError("Error while fetching quota extensions")
        }
       }
       fetchQuotaExtensions();
    },[isMobile])

    useEffect(() => {
      const selected = selectedQuota || quotaExtensions[0]; // fallback to first row
      if (selected) {
        const data = Object.keys(selected.quota || {}).map((key) => {
          const used = Number(selected.used_quota?.[key] || 0);
          const total = Number(selected.quota?.[key] || 0);
          const unused = total - used;
          return {
            name: key.charAt(0).toUpperCase() + key.slice(1),
            used,
            unused: unused < 0 ? 0 : unused,
          };
        });
        setQuotaData(data);
      } else {
        setQuotaData([]);
      }
    }, [quotaExtensions, selectedQuota]);



       const quotaExtensionsForGrid = quotaExtensions.map((p) => ({
        ...p,
        quota:
          typeof p.quota === "object" && p.quota !== null
            ? Object.entries(p.quota)
                .map(([k, v]) => `${k}: ${v}`)
                .join("\n")
            : p.quota,
        used_quota:
          typeof p.used_quota === "object" && p.used_quota !==null
            ? Object.entries(p.used_quota)
                .map(([k, v]) => `${k}: ${v}`)
                .join("\n")
            : p.used_quota
    
      }));
        const quotaExtensionRows = generateGridRows(quotaExtensionsForGrid,existingQuotaExtensionsRowMapping);
        const quotaExtensionColumns = generateGridColumns(
            existingQuotaExtensionsGridColumns,
            "quota_extension_id",
            "12px",
            quotaExtensionsOptionalColumns
        );
    
    const quotaAdditionalDetails = selectedQuota ? generateFormData(selectedQuota, quotaExtensionMapping) : null;

    const handleRowClick = (params) =>{
        setQuotaExtensionError(null);
        console.log("Row clicked:", params);
        if (selectedRowId === params.id) {
            setSelectedRowId(null);
            setSelectedQuota(null);
            return;
        }
        const quotaExtension = quotaExtensions[params.id - 1];

        setSelectedQuota(quotaExtension);
        setSelectedRowId(params.id);
        setActiveTab("details"); // Reset to "Details" tab on new selection
    }

    const handleCloseDetails = () => {
      setSelectedQuota(null);
      setSelectedRowId(null);
    }

    // Inline shared Tabs helper to avoid duplication
    function QuotaDetailsTabs({ activeTab, setActiveTab, details, data }) {
      return (
        <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="usage-tabs mb-3">
          <Tab
            eventKey="details"
            title={
              <span className="tab-title">
                <FiFileText style={{ marginRight: 6 }} />
                Details
              </span>
            }
          >
            <div className="usage-metric-details">
              <EntityDetails inputs={details} width={"150px"} />
            </div>
          </Tab>

          <Tab
            eventKey="quota"
            title={
              <span className="tab-title">
                <FiBarChart2 style={{ marginRight: 6 }} />
                Quota Chart
              </span>
            }
          >
            <div className="usage-tracker-chart">
              <QuotaBarChart data={data} />
            </div>
          </Tab>
        </Tabs>
      );
    }

 if (userRole !== "customer") {
        return (
        <div className="user-error">
            <h1>404 - Page Not Found</h1>
            <p>The page you're looking for doesn't exist.</p>
        </div>
        );
    }

    return(
        <div>
            <PageHelmet 
              title="Quota Extensions"
              description="Track user activities and actions within the application"
              url={`${process.env.REACT_APP_COGNITO_REDIRECT_URL}/add_ons`}
            />
            <div className={`add-ons-container ${selectedQuota ? 'with-selection' : 'no-selection'}`}>
               
                <div className="add-ons-grid">
                    <h3 className="add-ons-details-heading">Quota Extensions</h3>
                    {quotaExtensionError && (
                        <div>
                            <Alert variant="danger">{quotaExtensionError}</Alert> 
                        </div>
                    )}
                    <CustomDataGrid
                        rows={quotaExtensionRows}
                        columns={quotaExtensionColumns}
                        totalRows={quotaExtensionRows.length}
                        onRowClick={handleRowClick}
                        selectedRowId={selectedRowId}
                        getRowHeight={()=> "auto"}
                    />
                </div>
              
                {selectedQuota && (
                  <div className={`add-ons-details-wrapper ${isMobile ? 'mobile-open' : ''}`}>
                    <div className="add-ons-details-header">
                      <div className="mobile-detail-nav">
                        <button className="back-btn" onClick={handleCloseDetails} aria-label="Back">
                          <MdArrowBackIos size={18} />
                        </button>
                        <div className="mobile-activity-title">{selectedQuota?.quota_extension_id || selectedQuota?.subscription_id || 'Details'}</div>
                      </div>
                    </div>

                    <div className="add-ons-details-body">
                      <QuotaDetailsTabs activeTab={activeTab} setActiveTab={setActiveTab} details={quotaAdditionalDetails} data={quotaData} />
                    </div>
                  </div>
                )}
            </div>
        </div>
    )
}

export default QuotaExtensions;